import { useEffect, useRef, useState } from 'react'
import { api, screenId, type RadioSeat } from '../lib/api'

/** Where the panel lives, when the server has not said otherwise.
 *
 *  It has said otherwise since the beginning -- the token response carries the
 *  configured `base_url` -- and this constant was used anyway. That meant the
 *  browser leg ignored the console entirely: pointing a customer at a rebuilt
 *  panel on a new host would move the three server-to-server calls and the
 *  token's `aud` claim, and leave every dispatcher's iframe loading the old
 *  host, with the new server rejecting a sign-on whose audience did not match
 *  the origin that framed it. This is now the fallback, not the answer. */
export const PANEL_ORIGIN_FALLBACK = 'https://dispatch.advancetalk.ca'
const HEARTBEAT_MS = 30_000

/** The origin of a base URL, which is what postMessage compares against.
 *  A trailing path on the configured URL would otherwise never match an
 *  event's `origin` and every message from the panel would be dropped. */
function originOf(url: string | null | undefined): string {
  if (!url) return PANEL_ORIGIN_FALLBACK
  try {
    return new URL(url).origin
  } catch {
    return PANEL_ORIGIN_FALLBACK
  }
}

export type PanelTx = { at: string; who: string; mine: boolean }
export type PanelGroup = { id: string; name: string }

/** Holds the dispatch panel, once, for as long as the dispatcher is signed in.
 *
 *  MOUNTED EVEN WHEN HIDDEN, ON PURPOSE.
 *
 *  The dispatcher must be able to talk without looking at the panel, so the
 *  panel has to be running before they press anything. It is therefore mounted
 *  as soon as a seat is taken and simply hidden until asked for.
 *
 *  ONE SESSION, NOT TWO.
 *
 *  It would be easy to open the panel in a second window when the dispatcher
 *  wants it on another display. That is the one thing not to do: two panel
 *  windows in the same browser profile share localStorage, therefore share a
 *  WalkieFleet device id, and two fleet sessions on one device id makes
 *  recordings silently return nothing until the service is restarted. So the
 *  same frame is revealed in place instead.
 *
 *  THE CRM STILL HOLDS NO RADIO CONNECTION.
 *
 *  The panel inside this frame holds both of its own legs — one direct to
 *  WalkieFleet for voice, one to the provisioning server. This component only
 *  frames it and passes messages.
 */
export function PanelHost({
  visible,
  onSeat,
  onTx,
  onNote,
  onGroups,
  onWire,
  registerTransmit,
  registerSetGroup,
  standDown,
  enabled,
}: {
  visible: boolean
  /** False when this customer has no dispatch panel connection switched on.
   *  Then nothing here runs at all -- no seat is claimed, no hold is written
   *  into the append-only audit log, and no heartbeat ticks for a console that
   *  cannot open. */
  enabled: boolean
  onSeat: (seat: RadioSeat | null, seats: RadioSeat[]) => void
  onTx: (tx: PanelTx | null, ended?: PanelTx) => void
  onNote: (note: string | null) => void
  onGroups: (groups: PanelGroup[], selected: string | null) => void
  /** Every message the panel sends, raw and unfiltered.
   *
   *  Their protocol is documented in a handover that has been overtaken by
   *  three replies, so what the panel ACTUALLY sends is worth measuring rather
   *  than assuming. This is how we find out whether a message we need already
   *  exists under a name we did not expect. */
  onWire: (entry: { at: string; type: string; raw: string }) => void
  registerTransmit: (fn: ((on: boolean) => void) | null) => void
  registerSetGroup: (fn: ((id: string) => void) | null) => void
  /** The panel has been opened in another window. Unmount ours entirely --
   *  disabling the buttons is not enough, because two panel sessions in one
   *  browser profile share a WalkieFleet device id and that is what makes
   *  recordings come back empty. */
  standDown: boolean
}) {
  const [token, setToken] = useState<string | null>(null)
  const [seat, setSeat] = useState<RadioSeat | null>(null)
  // Where the server says the panel is. Not a constant: see above.
  const [panelBase, setPanelBase] = useState<string | null>(null)
  const frame = useRef<HTMLIFrameElement | null>(null)
  const ready = useRef(false)
  const screen = screenId()

  /** Take the seat on arrival. A dispatcher starting a shift should not have
   *  to ask for the radio. */
  async function arrive() {
    try {
      const res = await api.radioSignIn(screen)
      onSeat(res.connection ?? null, res.connections ?? [])
      if (res.connection && res.token) {
        setSeat(res.connection)
        setToken(res.token.token)
        setPanelBase(res.token.base_url)
        onNote(null)
      } else {
        setSeat(null)
        setToken(null)
        setPanelBase(null)
        onNote(res.reason ?? null)
      }
    } catch {
      setSeat(null)
      setToken(null)
      setPanelBase(null)
      onSeat(null, [])
    }
  }

  useEffect(() => {
    if (!enabled) {
      onSeat(null, [])
      return
    }
    arrive()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useEffect(() => {
    if (!seat) return
    const t = setInterval(async () => {
      try {
        const { held_by_me } = await api.radioHeartbeat(screen, seat.id)
        if (!held_by_me) {
          setToken(null)
          setSeat(null)
          await arrive()
        }
      } catch { /* one missed beat is not worth interrupting anybody */ }
    }, HEARTBEAT_MS)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seat])

  // Release on the way out so the next dispatcher is not waiting for a lease
  // to expire.
  //
  // Two ways out, and only the first was covered. Closing the tab fires
  // `pagehide`; SIGNING OUT does not -- it unmounts the tree, the effect's
  // cleanup quietly removes the listener without firing it, and the lease sat
  // there for its full two minutes with the hold row still open. Shift
  // handover is the normal case at a dispatch desk, so the arriving dispatcher
  // was routinely told the person who had just left was still holding the seat.
  const held = useRef<RadioSeat | null>(null)
  held.current = seat

  useEffect(() => {
    const release = () => {
      const s = held.current
      if (!s) return
      navigator.sendBeacon?.(
        '/api/radio/release',
        new Blob([JSON.stringify({ screen_id: screen, connection_id: s.id })],
                 { type: 'application/json' }),
      )
    }
    window.addEventListener('pagehide', release)
    return () => {
      window.removeEventListener('pagehide', release)
      release()   // unmounting IS leaving, and signing out is an unmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen])

  /** Ask the panel to key the microphone.
   *
   *  NOT WIRED YET, AND THE REASON IS THEIRS TO CHANGE. Their host-to-panel
   *  protocol carries host-hello, sign-out and ping — there is no transmit
   *  command. The message below is what we have asked them for; until it
   *  exists the panel ignores it, and the button that calls this stays
   *  disabled with the reason on screen rather than silently doing nothing.
   */
  const panelOrigin = originOf(panelBase)

  useEffect(() => {
    if (!token) {
      registerTransmit(null)
      return
    }
    const send = (on: boolean) => {
      const win = frame.current?.contentWindow
      if (!win || !ready.current) return
      win.postMessage(
        {
          protocol: 'advancetalk-dispatch',
          v: 1,
          msg: { type: 'transmit', phase: on ? 'start' : 'end' },
        },
        panelOrigin,
      )
    }
    registerTransmit(send)

    // Changing the talk group is the same story as transmitting: the panel
    // owns it, and there is no host-to-panel message for it yet.
    registerSetGroup((id: string) => {
      const win = frame.current?.contentWindow
      if (!win || !ready.current) return
      win.postMessage(
        { protocol: 'advancetalk-dispatch', v: 1,
          msg: { type: 'set-group', groupId: id } },
        panelOrigin,
      )
    })

    return () => {
      registerTransmit(null)
      registerSetGroup(null)
    }
  }, [token, panelOrigin, registerTransmit, registerSetGroup])

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== panelOrigin) return
      const { protocol, v, msg } = (e.data ?? {}) as {
        protocol?: string; v?: number
        msg?: { type?: string; phase?: string; target?: string
                destination?: string; at?: string; signedIn?: boolean }
      }
      // Logged before anything is matched, so an unrecognised message shows
      // up rather than being dropped in silence.
      onWire({
        at: new Date().toLocaleTimeString([], { hour12: false }),
        type: String(msg?.type ?? '(no type)'),
        raw: JSON.stringify(e.data),
      })

      if (protocol !== 'advancetalk-dispatch' || v !== 1 || !msg) return

      if (msg.type === 'ready' && e.source) {
        ready.current = true
        ;(e.source as Window).postMessage(
          { protocol, v: 1, msg: { type: 'host-hello', app: 'crm' } }, e.origin,
        )
        return
      }

      if (msg.type === 'auth' && msg.signedIn === false) {
        onGroups([], null)
        // Their reply 3: this now fires when the SERVER ends a session too, so
        // a suspension reaches us rather than leaving a name beside a dead
        // console.
        ready.current = false
        onNote('The dispatch panel signed out.')
        return
      }

      // Not shipped yet, and asked for: the panel's own talk groups, so the
      // strip offers what the panel actually has rather than a list we made up.
      if (msg.type === 'groups') {
        const list = (msg as unknown as { groups?: PanelGroup[] }).groups ?? []
        const sel = (msg as unknown as { selected?: string }).selected ?? null
        onGroups(list, sel)
        return
      }

      // Also not shipped: somebody else keying up. `tx` reports only this
      // dispatcher's own transmissions, so without this the strip cannot say
      // "U-12 is on the air".
      if (msg.type === 'air') {
        const who = (msg as unknown as { who?: string }).who
        const stamp = new Date(msg.at ?? Date.now()).toLocaleTimeString([], {
          hour: '2-digit', minute: '2-digit', hour12: false,
        })
        if (msg.phase === 'start' && who) onTx({ at: stamp, who, mine: false })
        else onTx(null)
        return
      }

      if (msg.type === 'tx') {
        const stamp = new Date(msg.at ?? Date.now()).toLocaleTimeString([], {
          hour: '2-digit', minute: '2-digit', hour12: false,
        })
        const who = msg.destination ?? msg.target ?? 'radio'
        // HE IS USING THE SCREEN (075): his own transmission keeps him signed
        // in -- the frame's clicks never reach this window.
        window.dispatchEvent(new Event('crm-use'))
        if (msg.phase === 'start') onTx({ at: stamp, who, mine: true })
        else onTx(null, { at: stamp, who, mine: true })
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [panelOrigin, onTx, onNote, onGroups, onWire])

  // Nothing mounted while the panel is open elsewhere. One session, always.
  // And nothing mounted at all without a token, which is what keeps the browser
  // from ever contacting the panel's origin once the connection is revoked.
  if (!enabled || standDown || !token || !seat) return null

  const src =
    `${panelOrigin}/?sso=${encodeURIComponent(token)}` +
    `&embedOrigin=${encodeURIComponent(window.location.origin)}`

  return (
    <div className={`panelhost ${visible ? 'shown' : ''}`}>
      {/* microphone and autoplay are required: without them the dispatcher
          cannot transmit and incoming calls are silent. */}
      <iframe
        ref={frame}
        title="Dispatch panel"
        src={src}
        allow="microphone; autoplay"
        className="panelframe"
      />
    </div>
  )
}
